/* ONE Other Ops card — the v1 card grammar
 * (ops-design-v1-2026-08-09.html), card chrome reused from the deck
 * surface (rkdecks.css: .card / .ctit / .hd / .ln / .abar / .btn /
 * .morelist / .slim).
 *
 * Ale ruling 2026-08-09 — this grammar is now the ADMIN VIEW. The three
 * machine-failure kinds — document_stuck · dead_letter_review ·
 * unroutable — are the SAME card: same header, same "what did not land"
 * band, same consequence line, same action bar. They differ only in what
 * failed and in which ruled verb takes it over; the server names the
 * verbs (`actions`) because the backend is the authority on what it will
 * accept.
 *
 * ADMIN keeps diagnosis, the per-leg PARTIAL retry (choosing which leg
 * is poison and re-running the savepoint-atomic write), discard, and the
 * raw payload of an unrouted type. The OFFICER gets only a one-click
 * whole-group Retry, on SystemFailureRow — and nothing at all for
 * `unroutable`. The daily-digest card left the officer view on
 * 2026-09-04 (audit cut 1, UI removal only): the acknowledgement rows keep
 * minting and closing server-side, unseen — this component renders the
 * admin diagnosis cards only.
 *
 * Every verb dispatches to machinery that ALREADY exists — retry and
 * discard to /tasks/{id}/stuck-action, close/acknowledge to
 * /tasks/{id}/close, resolve to /tasks/{id}/resolve. Nothing was
 * invented here. When the backend would refuse a verb for THIS row
 * (a document-targeted document_stuck has no event to re-mint; a
 * build-time drift dead-letter has no group to re-run) the refusal is
 * printed on the card and the button is disabled, rather than waiting
 * behind a click.
 *
 * NOT RENDERED, by ruling: `request_unlocked_copy` (a state of a
 * document on the Documents page — not an ops row, and not a chase row
 * either: a chase row is an open obligation and an unreadable document
 * has none), and parked orphans / byte duplicates for the same
 * document-state reason. Hand-entering a stuck document's facts is
 * Manual Entry, from the document — not a verb here. */

import { useState } from 'react'
import { closeTask, discardStuck, opsErrorText, outcomeLine, resolveTask, retryStuck } from './api'
import type { OpsCard as OpsCardData, OpsVerb } from './types'

interface Props {
  card: OpsCardData
  /** Called with the outcome line once a verb lands. */
  onDone: (slimText: string) => void
}

/** The primary verb per kind — the one the card leads with. */
const PRIMARY: Record<string, OpsVerb> = {
  document_stuck: 'retry',
  dead_letter_review: 'retry',
  unroutable: 'resolve',
}

/** Verbs that live in the ⋯ menu rather than the bar. */
const IN_MENU: OpsVerb[] = ['discard', 'leave_open']

function verbLabel(verb: OpsVerb, card: OpsCardData, excludedCount: number): string {
  switch (verb) {
    case 'retry':
      return excludedCount > 0 ? 'Retry with the ticked legs' : 'Retry the whole group'
    case 'discard':
      return 'Discard — reason required…'
    case 'close':
      return 'Close'
    case 'acknowledge':
      return 'Acknowledge'
    case 'resolve':
      return 'Resolve'
    case 'leave_open':
      return 'Leave open'
  }
}

export default function OpsCard({ card, onDone }: Props) {
  const [menuOpen, setMenuOpen] = useState(false)
  const [excluded, setExcluded] = useState<Record<string, boolean>>({})
  const [discarding, setDiscarding] = useState(false)
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [spent, setSpent] = useState(false)

  const excludedIds = card.legs
    .filter((leg) => leg.excludable && excluded[leg.proposal_id])
    .map((leg) => leg.proposal_id)

  const retryBlocked = Boolean(card.retry_blocked_reason)
  const barVerbs = card.actions.filter((verb) => !IN_MENU.includes(verb))
  const menuVerbs = card.actions.filter((verb) => IN_MENU.includes(verb))
  const primary = PRIMARY[card.kind]

  function finish(text: string, bad: boolean, isSpent: boolean) {
    if (isSpent) setSpent(true)
    onDone(`${bad ? '✗' : '✓'} ${text}`)
  }

  async function run(fn: () => Promise<void>) {
    setBusy(true)
    setError(null)
    try {
      await fn()
    } catch (err) {
      setError(opsErrorText(err))
    } finally {
      setBusy(false)
    }
  }

  function act(verb: OpsVerb) {
    setMenuOpen(false)
    if (verb === 'leave_open') {
      onDone('· left open')
      return
    }
    if (verb === 'discard') {
      setDiscarding(true)
      return
    }
    void run(async () => {
      if (verb === 'retry') {
        const result = await retryStuck(card.task_id, excludedIds)
        const line = outcomeLine(result)
        finish(line.text, line.bad, line.spent)
        return
      }
      if (verb === 'close' || verb === 'acknowledge') {
        await closeTask(card.task_id)
        finish(verb === 'acknowledge' ? 'acknowledged — the day was read' : 'closed', false, true)
        return
      }
      await resolveTask(card.task_id, note)
      finish('resolved — recorded under your name', false, true)
    })
  }

  function confirmDiscard() {
    void run(async () => {
      const result = await discardStuck(card.task_id, note.trim())
      const line = outcomeLine(result)
      setDiscarding(false)
      finish(line.text, line.bad, line.spent)
    })
  }

  return (
    <article
      className={`card${spent ? ' spent' : ''}`}
      data-testid="ops-card"
      data-kind={card.kind}
      data-task-id={card.task_id}
    >
      <div className="ctit">
        <span className="k" data-testid="ops-tag">
          {card.tag}
        </span>
        <span className="anch">{card.anchor_label}</span>
        {card.fund_name ? <span className="fund">{card.fund_name}</span> : null}
        <span
          className={`age${card.age_days > 30 ? ' warn' : ''}`}
          data-testid="ops-age"
          title={card.created_at}
        >
          {card.age_days === 0 ? 'today' : `${card.age_days} d`}
        </span>
      </div>

      {card.facts.length > 0 ? (
        <>
          <div className="hd">what did not land</div>
          {card.facts.map((fact) => (
            <div className="ln" key={`${fact.key}-${fact.value}`}>
              <span className="k">{fact.key}</span>
              <span className="v">
                {fact.value}
                {fact.note ? <small>{fact.note}</small> : null}
              </span>
            </div>
          ))}
        </>
      ) : null}

      {card.error_text ? (
        <div className="err" data-testid="ops-error-text">
          {card.error_text}
        </div>
      ) : null}

      {card.legs.length > 0 ? (
        <>
          <div className="hd">legs — untick any to exclude, the rest re-confirm</div>
          {card.legs.map((leg) => (
            <label className="leg" key={leg.proposal_id} data-testid="ops-leg">
              <input
                type="checkbox"
                checked={!excluded[leg.proposal_id]}
                disabled={!leg.excludable || busy}
                onChange={() =>
                  setExcluded((prev) => ({
                    ...prev,
                    [leg.proposal_id]: !prev[leg.proposal_id],
                  }))
                }
              />
              <span className="nm">{leg.label}</span>
              <span className={`st ${leg.poison ? 'bad' : 'ok'}`}>
                {excluded[leg.proposal_id]
                  ? 'excluded'
                  : leg.poison
                    ? 'the poison leg'
                    : 'would land'}
              </span>
            </label>
          ))}
        </>
      ) : null}

      {card.payload ? (
        <pre className="payload" data-testid="ops-payload">
          {JSON.stringify(card.payload, null, 2)}
        </pre>
      ) : null}

      {card.retry_blocked_reason ? (
        <div className="blocked" data-testid="ops-retry-blocked">
          {card.retry_blocked_reason}
        </div>
      ) : null}

      <div className="books">{card.consequence}</div>

      {error ? (
        <div className="errline" data-testid="ops-verb-error">
          {error}
        </div>
      ) : null}

      {discarding ? (
        <div className="discard" data-testid="ops-discard">
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            placeholder="Why is this being discarded? The reason is recorded and required."
            data-testid="ops-discard-note"
          />
          <div className="abar">
            <button
              type="button"
              className="btn rej"
              disabled={busy || !note.trim()}
              onClick={confirmDiscard}
              data-testid="ops-discard-confirm"
            >
              Discard
            </button>
            <button type="button" className="btn" onClick={() => setDiscarding(false)}>
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      <div className="abar">
        {barVerbs.map((verb) => {
          const isRetry = verb === 'retry'
          return (
            <button
              type="button"
              key={verb}
              className={`btn${verb === primary ? ' p' : ''}`}
              disabled={busy || spent || (isRetry && retryBlocked)}
              onClick={() => act(verb)}
              data-testid={`ops-verb-${verb}`}
            >
              {verbLabel(verb, card, excludedIds.length)}
            </button>
          )
        })}
        {menuVerbs.length > 0 ? (
          <div className="more">
            <button
              type="button"
              className="btn"
              onClick={() => setMenuOpen((open) => !open)}
              data-testid="ops-more"
              aria-expanded={menuOpen}
            >
              ⋯
            </button>
            {menuOpen ? (
              <div className="morelist" data-testid="ops-morelist">
                {menuVerbs.map((verb) => (
                  <div
                    key={verb}
                    role="button"
                    tabIndex={0}
                    className={verb === 'discard' ? 'danger' : undefined}
                    onClick={() => act(verb)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') act(verb)
                    }}
                    data-testid={`ops-verb-${verb}`}
                  >
                    {verbLabel(verb, card, excludedIds.length)}
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </article>
  )
}
